import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Resolve } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { share } from 'rxjs/operators';
import { HelpersService } from './helpers.service';
import { HttpQuery } from './http-query';
import { ProgressService } from './progress.service';
import { LoadingService } from './loading.service';

export interface BaseAPIObject<T> {
  data?: T;
  statusCode: number;
  message: string;
  errors?: any;
  meta?: any;
}

export interface BaseAPIArray<T> {
  data: T[];
  page: number;
  total: number;
  perPage?: number;
  lastPage?: number;
  nextPage?: string;
  previousPage?: string;
  statusCode: number;
  message: string;
  meta?: any;
}

@Injectable({
  providedIn: 'root',
})
export class BaseApiService<T> implements Resolve<T> {
  constructor(
    public readonly http: HttpClient,
    public readonly helper: HelpersService,
    public readonly loading: ProgressService,
    public readonly loadingService: LoadingService
  ) {
    this.baseUrl = '/api';
    this.onDataChanged = new BehaviorSubject(this.data);
    this.onDataLengthChanged = new BehaviorSubject(this.dataLength);
    this.onQueryChange = new BehaviorSubject(this.query);
    this.loading.setMode('query');
    setTimeout(() => {
      if (this.endpoint != '' && this.query) {
        this.onQueryChange.subscribe((query) => {
          this.query = query;
          // this.getAll(query)
        });
      }
    });
  }

  private baseUrl = '/api';
  endpoint = '';
  query: HttpQuery = {};
  protected data: T[] = [];
  protected dataLength: number = 0;
  protected currentData: T | any = {};
  onDataChanged: BehaviorSubject<any>;
  onDataLengthChanged: BehaviorSubject<any>;
  onQueryChange: BehaviorSubject<any>;

  resolve(): Observable<any> | Promise<any> | any {
    return this.getAll(this.query);
  }

  url(): string {
    return [this.baseUrl, this.endpoint].join('/');
  }

  url_id(id: string | number): string {
    return [this.baseUrl, this.endpoint, id].join('/');
  }

  url_custom(c: string): string {
    return [this.baseUrl, this.endpoint, c].join('/');
  }

  url_custom_id(c: string, id: string | number): string {
    return [this.baseUrl, this.endpoint, c, id].join('/');
  }

  url_array(c: string[]): string {
    const baseArray = [this.baseUrl, this.endpoint];
    c.map((a) => baseArray.push(a));
    return baseArray.join('/');
  }

  getAll(query?: HttpQuery, notifyChange: Boolean = true): Promise<T[]> {
    return new Promise((resolve, reject) => {
      const url = this.url();
      const params = query ? this.helper.generateParams(query) : {};
      if (Object.keys(params).length == 0) {
        return resolve(this.data);
      }
      this.loadingService.startLoading();
      this.http.get<BaseAPIArray<T>>(url, { params }).subscribe({
        next: ({ data, total }) => {
          this.data = data;
          this.dataLength = total;
          if (notifyChange) {
            this.onDataChanged.next(this.data);
            this.onDataLengthChanged.next(this.dataLength);
          }
          this.loadingService.endLoading();
          return resolve(this.data);
        },
        error: (err) => this.handleError(err, reject),
      });
    });
  }

  getDataLength(query?: HttpQuery, notifyChange: Boolean = true): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = this.url();
      const params = query ? this.helper.generateParams(query) : {};
      this.loadingService.startLoading();
      this.http.get<any>(url, { params }).subscribe({
        next: ({ total }) => {
          this.loadingService.endLoading();
          return resolve(total);
        },
        error: (err) => this.handleError(err, reject),
      });
    });
  }

  getAllData(): Promise<T | any> {
    return new Promise((resolve, reject) => {
      const url = this.url_custom('all');
      this.loadingService.startLoading();
      this.http
        .get<BaseAPIObject<T>>(url)
        .pipe(share())
        .subscribe({
          next: ({ data }) => {
            this.loadingService.endLoading();
            return resolve(data);
          },
          error: (err) => this.handleError(err, reject),
        });
    });
  }

  getById(id: string, query: HttpQuery = {}): Promise<T | any> {
    return new Promise((resolve, reject) => {
      const url = this.url_id(id);
      const params = query ? this.helper.generateParams(query) : {};
      if (Object.keys(params).length == 0) {
        return resolve(null);
      }
      this.loadingService.startLoading();
      this.http.get<BaseAPIObject<T>>(url, { params }).subscribe({
        next: ({ data }) => {
          this.currentData = data;
          this.loadingService.endLoading();
          return resolve(this.currentData);
        },
        error: (err) => this.handleError(err, reject),
      });
    });
  }

  getAllById(id: number, endpoint: string, param: string): Promise<T | any> {
    return new Promise((resolve, reject) => {
      const url = this.url_array(['all', endpoint]);
      let params = new HttpParams();
      params = params.append(param, id.toString());
      this.loadingService.startLoading();
      this.http.get<BaseAPIObject<T>>(url, { params }).subscribe({
        next: ({ data }) => {
          this.currentData = data;
          this.loadingService.endLoading();
          return resolve(this.currentData);
        },
        error: (err) => this.handleError(err, reject),
      });
    });
  }

  insert(data: any): Promise<T | any> {
    return new Promise((resolve, reject) => {
      const url = this.url();
      this.loadingService.startLoading();
      this.http
        .post<BaseAPIObject<T>>(url, data)
        .pipe(share())
        .subscribe({
          next: ({ data }) => {
            this.loadingService.endLoading();
            return resolve(data);
          },
          error: (err) => this.handleError(err, reject),
        });
    });
  }

  updateById(id: string, data: any): Promise<T | any> {
    return new Promise((resolve, reject) => {
      const url = this.url_id(id);
      this.loadingService.startLoading();
      this.http
        .put<BaseAPIObject<T>>(url, data)
        .pipe(share())
        .subscribe({
          next: ({ data }) => {
            this.loadingService.endLoading();
            return resolve(data);
          },
          error: (err) => this.handleError(err, reject),
        });
    });
  }

  removeById(id: string): Promise<any> {
    return new Promise((resolve, reject) => {
      const url = this.url_array([id]);
      this.loadingService.startLoading;
      this.http.delete<BaseAPIObject<T>>(url, {}).subscribe({
        next: ({ data }) => {
          this.loadingService.endLoading;
          return resolve(data);
        },
        error: (err) => this.handleError(err, reject),
      });
    });
  }

  handleError(err: any, reject: any) {
    this.loadingService.endLoading();
    // this.helper.sbError(err.error.message);
    return reject(err.error);
  }
}
